import React, { useState } from "react";
import SubscriptionService from "../../services/subscription";
import { toast } from "react-toastify";

/**
 * SubscriptionCheckout Component
 * Handles the creation of Razorpay subscriptions for recurring sponsorships
 */
const SubscriptionCheckout = ({
  kidData,
  monthsSelected,
  mentorData,
  categoryId,
  onSuccess,
  onError,
}) => {
  const [loading, setLoading] = useState(false);
  const [subscriptionCreated, setSubscriptionCreated] = useState(false);
  const [customerPhone, setCustomerPhone] = useState(
    mentorData?.mobile_no || mentorData?.phone || "",
  );
  const [customerEmail, setCustomerEmail] = useState(mentorData?.email || "");

  // Check if we need to collect contact info
  const needsContactInfo = !mentorData?.user_id || !mentorData?.email;

  /**
   * Initiate subscription creation with Razorpay Checkout
   */
  const initiateSubscription = async () => {
    if (loading) return;

    // Validate phone and email (phone required; international format allowed)
    if (!customerPhone || !String(customerPhone).trim()) {
      toast.error("Please enter your phone number");
      return;
    }
    if (!customerEmail || !customerEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      // Prepare subscription data
      const subscriptionPayload = {
        kid_id: kidData.id,
        duration_months: parseInt(monthsSelected),
        customer_name: mentorData?.name || mentorData?.full_name || "Sponsor",
        customer_email: customerEmail,
        customer_phone: customerPhone,
        mentor_id: mentorData?.user_id || null,
        is_anonymous: !mentorData?.user_id,
        category_id: categoryId || kidData.category_id,
        amount: kidData.total_expenses || kidData.basic_amount,
      };

      console.log("Creating subscription with data:", subscriptionPayload);

      // Create subscription via API
      const response =
        await SubscriptionService.createSubscription(subscriptionPayload);

      if (response.data.status && response.data.razorpay_subscription) {
        const subscription = response.data.razorpay_subscription;
        const subscriptionId = subscription.id; // Save subscription ID for use in handler

        // Use Razorpay Checkout instead of hosted page
        const options = {
          key: process.env.REACT_APP_RAZOR_KEY_ID,
          subscription_id: subscriptionId,
          name: "Shaagird",
          description: `Sponsor ${kidData.name} - Monthly`,
          image: "/logo.png",
          handler: async function (paymentResponse) {
            // Payment successful
            console.log("Payment successful:", paymentResponse);
            setSubscriptionCreated(true);
            toast.success("Subscription activated successfully!");

            try {
              // Verify and record payment for receipt generation
              // Use subscriptionId from closure, not from paymentResponse
              await SubscriptionService.verifyAndRecordPayment({
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_subscription_id: subscriptionId, // Use saved subscription ID
                razorpay_signature: paymentResponse.razorpay_signature,
                kid_id: kidData.id,
                mentor_id: mentorData?.user_id || null,
                customer_name:
                  mentorData?.name || mentorData?.full_name || "Sponsor",
                customer_email: customerEmail,
                customer_phone: customerPhone,
                amount: kidData.total_expenses || kidData.basic_amount,
                months: monthsSelected,
              });
              console.log("Payment recorded for receipt");
            } catch (verifyError) {
              console.error("Payment verification error:", verifyError);
              // Continue anyway as payment was successful
            }

            if (onSuccess) {
              onSuccess(response.data);
            }

            // Redirect to success page
            setTimeout(() => {
              window.location.href = `/success/${paymentResponse.razorpay_payment_id}`;
            }, 1000);
          },
          prefill: {
            name: mentorData?.name || mentorData?.full_name || "Sponsor",
            email: customerEmail,
            contact: customerPhone,
          },
          notes: {
            kid_id: kidData.id,
            kid_name: kidData.name,
          },
          theme: {
            color: "#FDC513",
          },
          modal: {
            ondismiss: function () {
              toast.info("Payment cancelled");
              setLoading(false);
            },
          },
        };

        // Load Razorpay checkout
        const razorpay = new window.Razorpay(options);
        razorpay.on("payment.failed", function (failResponse) {
          console.error("Payment failed:", failResponse.error);
          toast.error(`Payment failed: ${failResponse.error.description}`);
          setLoading(false);
          if (onError) {
            onError(failResponse.error);
          }
        });
        razorpay.open();
      } else {
        throw new Error(
          response.data.message || "Failed to create subscription",
        );
      }
    } catch (error) {
      console.error("Subscription creation failed:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create subscription. Please try again.";

      toast.error(errorMessage);
      setLoading(false);

      if (onError) {
        onError(error);
      }
    }
  };

  // Calculate totals
  const monthlyAmount = kidData?.total_expenses || kidData?.basic_amount || 0;
  const totalAmount = monthlyAmount * parseInt(monthsSelected || 0);

  // Styles
  const styles = {
    container: {
      padding: "20px",
      backgroundColor: "#f8f9fa",
      borderRadius: "12px",
      marginTop: "16px",
    },
    summaryTitle: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#333",
      marginBottom: "16px",
      borderBottom: "1px solid #eee",
      paddingBottom: "8px",
    },
    summaryItem: {
      display: "flex",
      justifyContent: "space-between",
      padding: "8px 0",
      fontSize: "14px",
    },
    label: {
      color: "#666",
    },
    value: {
      fontWeight: "500",
      color: "#333",
    },
    totalRow: {
      borderTop: "1px solid #ddd",
      marginTop: "8px",
      paddingTop: "12px",
      fontSize: "16px",
      fontWeight: "600",
    },
    benefits: {
      backgroundColor: "#e8f5e9",
      padding: "12px",
      borderRadius: "8px",
      marginTop: "16px",
    },
    benefitsTitle: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#2e7d32",
      marginBottom: "8px",
    },
    benefitsList: {
      listStyle: "none",
      padding: 0,
      margin: 0,
      fontSize: "12px",
      color: "#388e3c",
    },
    benefitItem: {
      padding: "4px 0",
    },
    inputGroup: {
      marginBottom: "12px",
    },
    inputLabel: {
      display: "block",
      fontSize: "12px",
      color: "#666",
      marginBottom: "4px",
    },
    input: {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #ddd",
      borderRadius: "8px",
      fontSize: "14px",
      boxSizing: "border-box",
    },
    button: {
      width: "100%",
      padding: "14px",
      backgroundColor: "#FDC513",
      color: "#333",
      border: "none",
      borderRadius: "8px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      marginTop: "16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: "not-allowed",
    },
    note: {
      fontSize: "11px",
      color: "#888",
      textAlign: "center",
      marginTop: "12px",
    },
    success: {
      textAlign: "center",
      padding: "20px",
    },
    successIcon: {
      fontSize: "48px",
      marginBottom: "12px",
    },
    spinner: {
      width: "16px",
      height: "16px",
      border: "2px solid #333",
      borderTopColor: "transparent",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
  };

  return (
    <div style={styles.container}>
      <h4 style={styles.summaryTitle}>🔄 Monthly Subscription</h4>

      <div style={styles.summaryItem}>
        <span style={styles.label}>Duration:</span>
        <span style={styles.value}>
          {monthsSelected} {monthsSelected == 1 ? "month" : "months"}
        </span>
      </div>

      <div style={styles.summaryItem}>
        <span style={styles.label}>Monthly Amount:</span>
        <span style={styles.value}>₹{monthlyAmount?.toLocaleString()}</span>
      </div>

      <div style={{ ...styles.summaryItem, ...styles.totalRow }}>
        <span style={styles.label}>Total Commitment:</span>
        <span style={styles.value}>₹{totalAmount?.toLocaleString()}</span>
      </div>

      <div style={styles.benefits}>
        <div style={styles.benefitsTitle}>✨ Auto-Pay Benefits</div>
        <ul style={styles.benefitsList}>
          <li style={styles.benefitItem}>✓ Automatic monthly payments</li>
          <li style={styles.benefitItem}>
            ✓ Cancel anytime from your dashboard
          </li>
          <li style={styles.benefitItem}>
            ✓ Email notifications before each charge
          </li>
        </ul>
      </div>

      {/* Contact Info Fields */}
      <div style={{ marginTop: "16px" }}>
        <div style={styles.inputGroup}>
          <label style={styles.inputLabel}>Email *</label>
          <input
            type="email"
            style={styles.input}
            placeholder="your@email.com"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
          />
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.inputLabel}>Phone Number *</label>
          <input
            type="tel"
            style={styles.input}
            placeholder="e.g. 9876543210 or +44 20 7123 4567"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
          />
        </div>
      </div>

      {subscriptionCreated ? (
        <div style={styles.success}>
          <div style={styles.successIcon}>✅</div>
          <h4>Subscription Created!</h4>
          <p>Redirecting to payment page...</p>
        </div>
      ) : (
        <button
          onClick={initiateSubscription}
          disabled={loading || !monthsSelected || monthsSelected == 0}
          style={{
            ...styles.button,
            ...(loading ? styles.buttonDisabled : {}),
          }}
        >
          {loading ? (
            <>Creating Subscription...</>
          ) : (
            <>Subscribe & Pay ₹{monthlyAmount?.toLocaleString()}/month</>
          )}
        </button>
      )}

      <p style={styles.note}>
        You will be charged ₹{monthlyAmount?.toLocaleString()} every month for{" "}
        {monthsSelected} months. First payment will be processed immediately.
      </p>
    </div>
  );
};

export default SubscriptionCheckout;
